import React, { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { Link } from 'react-router-dom';
import type {
  PriceItemWithRelations,
  DraggedPriceItemItem
} from '@/shared/types';
import { DRAG_TYPES } from '@/shared/types';

/**
 * Пропсы для компонента DraggablePriceItemRow
 */
interface DraggablePriceItemRowProps {
  item: PriceItemWithRelations;
  index: number;
  onMove: (dragIndex: number, hoverIndex: number) => void;
  onDragEnd: () => Promise<void>;
  onStatusChange: (item: PriceItemWithRelations) => Promise<void>;
  formatPrice: (price: string | null) => string;
}

/**
 * Компонент перетаскиваемой строки услуги
 * Реализует drag-and-drop функциональность для изменения порядка услуг
 * @param item - Данные услуги
 * @param index - Индекс услуги в списке
 * @param onMove - Функция перемещения услуги
 * @param onDragEnd - Функция завершения перетаскивания
 * @param onStatusChange - Функция изменения статуса услуги
 * @param formatPrice - Функция форматирования цены
 * @returns JSX элемент
 */
export const DraggablePriceItemRow: React.FC<DraggablePriceItemRowProps> = ({
  item,
  index,
  onMove,
  onDragEnd,
  onStatusChange,
  formatPrice
}) => {
  const ref = useRef<HTMLTableRowElement>(null);

  /**
   * Настройка drag функциональности
   */
  const [{ isDragging }, drag] = useDrag({
    type: DRAG_TYPES.PRICE_ITEM_ROW,
    item: (): DraggedPriceItemItem => ({
      type: DRAG_TYPES.PRICE_ITEM_ROW,
      id: item.id,
      index,
      sortOrder: item.sortOrder
    }),
    end: (_item, monitor) => {
      // Вызываем обновление порядка сортировки при завершении перетаскивания
      if (monitor.didDrop()) {
        onDragEnd();
      }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  });

  /**
   * Настройка drop функциональности
   */
  const [, drop] = useDrop({
    accept: DRAG_TYPES.PRICE_ITEM_ROW,
    hover: (draggedItem: DraggedPriceItemItem, monitor) => {
      if (!ref.current) {
        return;
      }

      const dragIndex = draggedItem.index;
      const hoverIndex = index;

      // Не делать ничего, если элемент перетаскивается на самого себя
      if (dragIndex === hoverIndex) {
        return;
      }

      // Определить прямоугольник элемента на экране
      const hoverBoundingRect = ref.current.getBoundingClientRect();

      // Получить вертикальную середину
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Определить позицию мыши
      const clientOffset = monitor.getClientOffset();

      if (!clientOffset) {
        return;
      }

      // Получить пиксели до верха
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      // Перетаскивание вниз
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Перетаскивание вверх
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      // Выполнить перемещение
      onMove(dragIndex, hoverIndex);

      // Обновить индекс для следующих вызовов
      draggedItem.index = hoverIndex;
    }
  });

  // Подключить drag и drop к элементу
  drag(drop(ref));

  return (
    <tr
      ref={ref}
      className={`${isDragging ? 'opacity-50' : 'opacity-100'} transition-opacity duration-200`}
      style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
    >
      {/* Drag handle */}
      <td className="w-8">
        <div className="flex items-center justify-center">
          <div className="cursor-grab hover:cursor-grabbing text-base-content/40 hover:text-base-content/60 transition-colors">
            <i className="fas fa-grip-vertical text-sm"></i>
          </div>
        </div>
      </td>

      {/* Название услуги */}
      <td>
        <div className="font-medium text-base-content">{item.name}</div>
      </td>

      {/* Врач */}
      <td>
        {item.doctor ? (
          <Link
            to={`/doctors/${item.doctor.id}/edit`}
            className="link link-primary hover:link-hover"
          >
            {item.doctor.fullName}
          </Link>
        ) : (
          <span className="text-base-content/60">—</span>
        )}
      </td>

      {/* Цена очно */}
      <td>
        <span className="font-mono text-sm">
          {formatPrice(item.priceOffline)}
        </span>
      </td>

      {/* Цена онлайн */}
      <td>
        <span className="font-mono text-sm">
          {formatPrice(item.priceOnline)}
        </span>
      </td>

      {/* Статус */}
      <td>
        {item.isActive ? (
          <span className="badge badge-success">Активна</span>
        ) : (
          <span className="badge badge-error">Неактивна</span>
        )}
      </td>

      {/* Порядок сортировки */}
      <td>
        <span className="text-sm text-base-content/60">{item.sortOrder}</span>
      </td>

      {/* Действия */}
      <td>
        <div className="flex gap-2">
          <Link
            to={`/prices/items/${item.id}/edit`}
            className="btn btn-primary btn-sm"
            title="Редактировать"
          >
            <i className="fas fa-edit"></i>
          </Link>
          <button
            onClick={() => onStatusChange(item)}
            className={`btn btn-sm ${
              item.isActive ? 'btn-warning' : 'btn-success'
            }`}
            title={item.isActive ? 'Деактивировать' : 'Активировать'}
          >
            <i className={`fas ${item.isActive ? 'fa-pause' : 'fa-play'}`}></i>
          </button>
        </div>
      </td>
    </tr>
  );
};
